import {VueController} from '@/service/support/vuex-controller';
import orgService from "@/service/org-service";

export default VueController("ApiSettings", {
	state: [
		"organization",
		"organizationId",
		"cognito",
		"user"
	],
	data() {
		return {
			loading: false,
			togglingApi: false,
			apiEnabledToggle: false,
			oauthClients: [],
			clientHeaders: [
				{
					text: "Client Name",
					align: 'start',
					sortable: true,
					value: 'name'
				},
				{
					text: "Client ID",
					value: 'client_id'
				},
				{
					text: "Allowed Scopes",
					value: 'allowed_scopes'
				},
				{
					text: "Created",
					value: 'created_at'
				},
				{
					text: "Actions",
					value: 'actions',
					sortable: false
				}
			],
			showNewClientDialog: false,
			showClientSecretDialog: false,
			newClient: {
				name: '',
				redirect_uris: [''],
				allowed_scopes: []
			},
			createdClientSecret: '',
			createdClientId: '',
			saving: false,
			deleting: {},
			availableScopes: [
				{ value: 'portal:read', text: 'Read Portals', description: 'List and view portals' },
				{ value: 'portal:write', text: 'Write Portals', description: 'Create and update portals' },
				{ value: 'file:list', text: 'List Files', description: 'List file names and metadata (not contents)' },
				{ value: 'file:upload', text: 'Upload Files', description: 'Upload new files to portals' },
				{ value: 'task:read', text: 'Read Tasks', description: 'List tasks' },
				{ value: 'task:write', text: 'Write Tasks', description: 'Create, update, and complete tasks' },
				{ value: 'guest:invite', text: 'Invite Guests', description: 'Invite guests to portals' },
				{ value: 'webhook:manage', text: 'Manage Webhooks', description: 'Register and manage webhooks' }
			],
			scopeRules: {
				required: value => (value && value.length > 0) || 'At least one scope is required'
			},
			nameRules: {
				required: value => (value && value.trim().length > 0) || 'Client name is required',
				maxLength: value => (value && value.length <= 100) || 'Client name must be 100 characters or less'
			},
			redirectUriRules: {
				required: value => (value && value.trim().length > 0) || 'Redirect URI is required',
				validUrl: value => {
					if (!value) return true;
					try {
						const url = new URL(value);
						return (url.protocol === 'http:' || url.protocol === 'https:') || 'Must be a valid HTTP or HTTPS URL';
					} catch (e) {
						return 'Must be a valid URL';
					}
				}
			}
		};
	},
	computed: {
		canManageApi() {
			if (!this.organization) return false;
			// Only admins can manage API settings
			const userType = this.cognito && this.cognito.attributes ? this.cognito.attributes["custom:mutableUserType"] : null;
			return userType === 'admin';
		},
		apiEnabled() {
			return this.organization && this.organization.apiEnabled ? this.organization.apiEnabled : false;
		},
		formattedClients() {
			return this.oauthClients.map(client => ({
				...client,
				scopes_display: client.allowed_scopes ? client.allowed_scopes.join(', ') : '',
				created_display: client.created_at ? new Date(client.created_at).toLocaleDateString() : ''
			}));
		},
		isNewClientFormValid() {
			if (!this.newClient.name || this.newClient.name.trim().length === 0) return false;
			if (!this.newClient.allowed_scopes || this.newClient.allowed_scopes.length === 0) return false;
			if (!this.newClient.redirect_uris || this.newClient.redirect_uris.length === 0) return false;
			
			// Check all redirect URIs are valid
			for (let uri of this.newClient.redirect_uris) {
				if (!uri || uri.trim().length === 0) return false;
				try {
					const url = new URL(uri);
					if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
				} catch (e) {
					return false;
				}
			}
			
			return true;
		}
	},
	mounted() {
		this.apiEnabledToggle = this.apiEnabled;
		this.loadOAuthClients();
	},
	watch: {
		apiEnabled(newVal) {
			this.apiEnabledToggle = newVal;
		}
	},
	methods: {
		async toggleApiEnabled() {
			this.togglingApi = true;
			
			try {
				await orgService.mutation("updateOrganizationBoolean", {
					field: "apiEnabled",
					value: this.apiEnabledToggle
				});
				
				// Update local state
				this.$store.commit('setOrganization', {
					...this.organization,
					apiEnabled: this.apiEnabledToggle
				});
				
				this.notification('success', this.apiEnabledToggle ? 'API access enabled' : 'API access disabled');
			} catch (error) {
				console.error("Error toggling API access:", error);
				// Revert the toggle on error
				this.apiEnabledToggle = !this.apiEnabledToggle;
				this.notification('error', 'Failed to update API access: ' + (error.message || 'Unknown error'));
			} finally {
				this.togglingApi = false;
			}
		},
		
		loadOAuthClients() {
			if (!this.canManageApi) return;
			
			this.loading = true;
			orgService.query("getOAuthClients", undefined, undefined, {vuexCache: false})
				.then(response => {
					this.oauthClients = response || [];
					this.loading = false;
				})
				.catch(error => {
					console.error("Error loading OAuth clients:", error);
					this.notification('error', 'Failed to load API clients');
					this.loading = false;
				});
		},
		
		openNewClientDialog() {
			this.newClient = {
				name: '',
				redirect_uris: [''],
				allowed_scopes: []
			};
			this.showNewClientDialog = true;
		},
		
		addRedirectUri() {
			this.newClient.redirect_uris.push('');
		},
		
		removeRedirectUri(index) {
			if (this.newClient.redirect_uris.length > 1) {
				this.newClient.redirect_uris.splice(index, 1);
			}
		},
		
		async createOAuthClient() {
			if (!this.isNewClientFormValid) {
				this.notification('error', 'Please fill in all required fields correctly');
				return;
			}
			
			this.saving = true;
			
			try {
				const response = await orgService.mutation("createOAuthClient", {
					name: this.newClient.name.trim(),
					redirect_uris: this.newClient.redirect_uris.filter(uri => uri && uri.trim().length > 0).map(uri => uri.trim()),
					allowed_scopes: this.newClient.allowed_scopes
				});
				
				this.createdClientId = response.client_id;
				this.createdClientSecret = response.client_secret;
				this.showNewClientDialog = false;
				this.showClientSecretDialog = true;
				
				this.notification('success', 'OAuth client created successfully');
				this.loadOAuthClients();
			} catch (error) {
				console.error("Error creating OAuth client:", error);
				this.notification('error', 'Failed to create OAuth client: ' + (error.message || 'Unknown error'));
			} finally {
				this.saving = false;
			}
		},
		
		copyToClipboard(text, label) {
			if (!text) return;
			
			navigator.clipboard.writeText(text).then(() => {
				this.notification('success', label + ' copied to clipboard');
			}).catch(err => {
				console.error('Failed to copy:', err);
				this.notification('error', 'Failed to copy to clipboard');
			});
		},
		
		async deleteClient(client) {
			if (!confirm('Are you sure you want to delete this OAuth client? This action cannot be undone and will invalidate all access tokens for this client.')) {
				return;
			}
			
			this.$set(this.deleting, client.client_id, true);
			
			try {
				await orgService.mutation("deleteOAuthClient", {
					client_id: client.client_id
				});
				
				this.notification('success', 'OAuth client deleted successfully');
				this.loadOAuthClients();
			} catch (error) {
				console.error("Error deleting OAuth client:", error);
				this.notification('error', 'Failed to delete OAuth client: ' + (error.message || 'Unknown error'));
			} finally {
				this.$set(this.deleting, client.client_id, false);
			}
		},
		
		closeClientSecretDialog() {
			this.showClientSecretDialog = false;
			this.createdClientId = '';
			this.createdClientSecret = '';
		},
		
		getScopeDescription(scopeValue) {
			const scope = this.availableScopes.find(s => s.value === scopeValue);
			return scope ? scope.description : scopeValue;
		}
	}
});

